import { Box, FormLabel, IconButton, InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import React, { useMemo, useState } from 'react';
import { BaseTextField } from '../BaseTextField';
import { Clear, Search } from '@mui/icons-material';
import { BaseListBoxOption, ListBox, ListBoxProps } from './ListBox.component';
import { SelectionOverview, SelectionOverviewProps } from './SelectionOverview.component';
import { ListContainedContainer } from './ListContainedContainer.component';
import { ScrollableView } from '../../Layout';


export type SearchListContainedProps<Option extends BaseListBoxOption> = {
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  fullPageMobileView?: boolean;
} & ListBoxProps<Option> & Omit<SelectionOverviewProps, 'selectedCount'>;

export const SearchListContained = <Option extends BaseListBoxOption>({
  color = 'secondary',
  options,
  selectionOverviewText,
  ...props
}: SearchListContainedProps<Option>) => {
  const { classes } = useSearchListContainedStyles();
  const [ field, meta ] = useField<string[]>(props.name);
  const [ inputValue, setInputValue ] = useState('');
  const selectedIds = useMemo(() => {
    return field.value;
  }, [ field.value ]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      const label = props.getOptionLabel(option);

      return label.toLowerCase().includes(inputValue.toLowerCase());
    });
  }, [ inputValue, options, props ]);

  const startAdornment = useMemo(() => {
    return (
      <InputAdornment position="start">
        <Search className={classes.searchIcon} />
      </InputAdornment>
    );
  }, [ classes.searchIcon ]);

  const endAdornment = useMemo(() => {
    if (inputValue) {
      return (
        <InputAdornment position="end">
          <IconButton onClick={() => setInputValue('')}>
            <Clear />
          </IconButton>
        </InputAdornment>
      );
    }

    return null;
  }, [ inputValue ]);

  const hasError = Boolean(props.error && props.helperText) || Boolean(meta.error && meta.touched);
  const helperText = (props.helperText || (meta.touched && meta.error));

  return (
    <ListContainedContainer
      hasError={hasError}
      helperText={helperText}
      label={props.fullPageMobileView ? undefined : props.label}
      fullPageMobileView={props.fullPageMobileView}
    >
      <Box className={classes.inputContainer}>
        <BaseTextField
          disabled={props.loading || props.disabled}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          fullWidth
          className={classes.inputRoot}
          slotProps={{
            input: {
              classes: {
                notchedOutline: classes.notchedOutline,
                input: classes.input,
                adornedEnd: classes.adornedEnd
              },
              startAdornment: startAdornment,
              endAdornment: endAdornment,
            }
          }}
          placeholder={props.placeholder}
        />
      </Box>

      <ScrollableView flex="1">
        {props.fullPageMobileView && props.label && (
          <Box my={1} px={2}>
            <FormLabel focused={false}>{props.label}</FormLabel>
          </Box>
        )}
        <ListBox
          options={filteredOptions}
          color={color}
          {...props}
        />
      </ScrollableView>

      <SelectionOverview
        fullScreen={props.fullPageMobileView}
        loading={props.loading}
        selectionOverviewText={selectionOverviewText}
        selectedCount={selectedIds.length}
        color={color}
      />
    </ListContainedContainer>
  );
};

const useSearchListContainedStyles = makeStyles({ name: 'SearchListContained' })(theme => ({
  inputContainer: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    boxShadow: `inset 0px -1px 0px ${theme.palette.divider}`,
  },
  inputRoot: {
    flexDirection: 'unset'
  },
  input: {
    '&::placeholder': {
      fontStyle: 'italic'
    },
  },
  notchedOutline: {
    border: 'unset !important'
  },
  adornedEnd: {
    paddingRight: 0,
  },
  searchIcon: {
    color: theme.palette.text.secondary
  },
  clearAdornment: {
    marginRight: theme.spacing(-1.5)
  },
}));
